import { useCallback, useEffect, useState } from 'react';

import client from '../api/client';

// --- raw API wrappers ---------------------------------------------------
// Exposed as standalone functions so the ProductEditor (a separate route)
// can create/update/manage photos without pulling in the list hook.

export async function fetchAdminProduct(id) {
  // GET /product/:id returns the doc (category populated) regardless of the
  // active flag, so an admin can edit an inactive product.
  const { data } = await client.get(`/product/${id}`);
  return data;
}

export async function createProduct(body) {
  // body: { title, description, category (id|slug), price (int cents), active? }
  const { data } = await client.post('/product', body);
  return data;
}

export async function updateProduct(id, body) {
  // body: any of { title, description, category, price, active, photoOrder }
  const { data } = await client.put(`/product/${id}`, body);
  return data;
}

export async function deleteProduct(id) {
  // Soft delete (Cloudinary assets kept).
  await client.delete(`/product/${id}`);
}

export async function addProductPhotos(id, files) {
  // Multipart field name is 'photos' (max 10, appended to product.photos[]).
  const form = new FormData();
  Array.from(files).forEach((f) => form.append('photos', f));
  const { data } = await client.post(`/product/${id}/photos`, form);
  return data;
}

export async function removeProductPhoto(id, publicId) {
  // publicId contains slashes and maps to the server's splat route
  // (/product/:id/photos/*) — it must stay unescaped as path segments.
  const { data } = await client.delete(`/product/${id}/photos/${publicId}`);
  return data;
}

export async function reorderProductPhotos(id, orderedPublicIds) {
  // Reorder is done through PUT with photoOrder = [publicId, ...].
  const { data } = await client.put(`/product/${id}`, { photoOrder: orderedPublicIds });
  return data;
}

// --- list hook for the admin products page ------------------------------
// GET /product?includeInactive=true -> { items, page, limit, total, pages }.

const EMPTY = { items: [], page: 1, limit: 20, total: 0, pages: 1 };

export default function useAdminProducts({ page = 1, limit = 20, q = '', sort = '', includeInactive = true } = {}) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const buildParams = useCallback(() => {
    const params = { includeInactive, page, limit };
    if (q) params.q = q;
    if (sort) params.sort = sort;
    return params;
  }, [page, limit, q, sort, includeInactive]);

  const reload = useCallback(async () => {
    const { data: res } = await client.get('/product', { params: buildParams() });
    setData(res && Array.isArray(res.items) ? res : EMPTY);
  }, [buildParams]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    client
      .get('/product', { params: buildParams() })
      .then(({ data: res }) => {
        if (active) setData(res && Array.isArray(res.items) ? res : EMPTY);
      })
      .catch((err) => {
        if (active) {
          setData(EMPTY);
          setError(err);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [buildParams]);

  const update = useCallback(
    async (id, body) => {
      const res = await updateProduct(id, body);
      await reload();
      return res;
    },
    [reload]
  );

  const remove = useCallback(
    async (id) => {
      await deleteProduct(id);
      await reload();
    },
    [reload]
  );

  return { ...data, loading, error, reload, update, remove };
}
